import React, { useState } from 'react';
import { Coins, Search, Plus, Minus, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const PointsManagementTab = ({ 
  user,
  loading, 
  setLoading,
  language,
  t,
  showSuccessToast,
  showErrorToast,
  loadData,
  onUserUpdate
}) => {
  // Points management state
  const [pointsManagement, setPointsManagement] = useState({
    searchTerm: '',
    searchResults: [],
    selectedUser: null,
    isSearching: false,
    actionType: 'add', // 'add', 'remove', 'set'
    pointsAmount: '',
    reason: '',
    showConfirmModal: false
  });

  // Points management search function
  const searchUsersForPoints = async (searchTerm) => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setPointsManagement(prev => ({ ...prev, searchResults: [], isSearching: false }));
      return;
    }

    setPointsManagement(prev => ({ ...prev, isSearching: true }));

    try {
      const response = await axios.get(`/api/users/search?q=${searchTerm}`);
      setPointsManagement(prev => ({ 
        ...prev, 
        searchResults: response.data,
        isSearching: false 
      }));
    } catch (error) {
      console.error('Search users for points error:', error);
      setPointsManagement(prev => ({ ...prev, searchResults: [], isSearching: false }));
      showErrorToast(t('failedToSearchUsers', language));
    }
  };

  // Handle points management
  const handlePointsManagement = async () => {
    const { selectedUser, actionType, pointsAmount, reason } = pointsManagement;
    
    if (!selectedUser || !pointsAmount || isNaN(pointsAmount) || parseInt(pointsAmount) <= 0) {
      showErrorToast(t('pleaseEnterValidAmount', language));
      return;
    }

    const points = parseInt(pointsAmount);
    
    try {
      setLoading(true);
      
      let endpoint, data;
      switch (actionType) {
        case 'add':
          endpoint = '/api/users/add-points';
          data = { userId: selectedUser._id, points };
          break;
        case 'remove':
          endpoint = '/api/users/add-points';
          data = { userId: selectedUser._id, points: -points };
          break;
        case 'set':
          endpoint = '/api/users/set-points';
          data = { userId: selectedUser._id, points };
          break;
        default:
          throw new Error('Invalid action type');
      }

      await axios.post(endpoint, data);

      const successKey = actionType === 'add' ? 'pointsAddedSuccessfully' : 
                        actionType === 'remove' ? 'pointsRemovedSuccessfully' : 
                        'pointsSetSuccessfully';
      
      showSuccessToast(t(successKey, language, { points, username: selectedUser.inGameName }));
      
      // Reset form
      setPointsManagement({
        searchTerm: '',
        searchResults: [],
        selectedUser: null,
        isSearching: false,
        actionType: 'add',
        pointsAmount: '',
        reason: '',
        showConfirmModal: false
      });
      
      // Reload data
      loadData();
      onUserUpdate();

    } catch (error) {
      const message = error.response?.data?.message || t('failedToManagePoints', language);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value;
    setPointsManagement(prev => ({ ...prev, searchTerm }));
    searchUsersForPoints(searchTerm);
  };

  const selectUser = (user) => {
    setPointsManagement(prev => ({
      ...prev,
      selectedUser: user,
      searchResults: [],
      searchTerm: user.inGameName
    }));
  };

  const openConfirmModal = () => {
    if (!pointsManagement.selectedUser) {
      toast.error(t('pleaseSelectUser', language));
      return;
    }
    if (!pointsManagement.pointsAmount || isNaN(pointsManagement.pointsAmount) || parseInt(pointsManagement.pointsAmount) <= 0) {
      toast.error(t('pleaseEnterValidAmount', language));
      return;
    }
    setPointsManagement(prev => ({ ...prev, showConfirmModal: true }));
  };

  return (
    <div className="role-management">
      <div className="role-management-section">
        <h4 className="role-management-title">
          <Coins className="w-5 h-5" />
          {t('pointsManagement', language)}
        </h4>
        <p className="role-management-description">{t('searchUsersManagePoints', language)}</p>
        
        <div className="role-management-grid">
          {/* Search Section */}
          <div className="role-search-section">
            <div>
              <label className="role-search-label">
                {t('searchUsers', language)}
              </label>
              <div className="role-search-container">
                <Search className="role-search-icon" />
                <input
                  type="text"
                  placeholder={t('searchByUsername', language)}
                  value={pointsManagement.searchTerm}
                  onChange={handleSearchChange}
                  className="role-search-input"
                />
                {pointsManagement.isSearching && (
                  <div className="role-search-spinner">
                    <div className="search-spinner"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Search Results */}
            {pointsManagement.searchResults.length > 0 && (
              <div className="role-search-results">
                <div className="role-search-results-header">
                  <p className="role-search-results-title">{t('searchResults', language)} ({pointsManagement.searchResults.length})</p>
                </div>
                {pointsManagement.searchResults.map(user => (
                  <div
                    key={user._id}
                    onClick={() => selectUser(user)}
                    className="role-search-result-item"
                  >
                    <div className="role-search-result-content">
                      <div className="role-search-result-info">
                        <p className="role-search-result-name">{user.inGameName}</p>
                        <p className="role-search-result-email">{user.email}</p>
                        <p className="role-search-result-stats">{user.points} {t('points', language)} • {user.wins}W-{user.losses}L</p>
                      </div>
                      <div className="points-display">
                        <span className="points-badge">{user.points}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Points Management Section */}
          <div className="role-selection-section">
            {pointsManagement.selectedUser ? (
              <>
                <div className="selected-user-card">
                  <h5 className="selected-user-title">{t('selectedUser', language)}</h5>
                  <div className="selected-user-info">
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('username', language)}:</span> 
                      <span className="selected-user-info-value">{pointsManagement.selectedUser.username}</span>
                    </div>
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('inGameName', language)}:</span> 
                      <span className="selected-user-info-value">{pointsManagement.selectedUser.inGameName}</span>
                    </div>
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('currentPoints', language)}:</span> 
                      <span className="selected-user-info-value points">{pointsManagement.selectedUser.points}</span>
                    </div>
                  </div>
                </div>

                <div className="points-action-grid">
                  <div className="role-select-group">
                    <label className="role-select-label">{t('actionType', language)}</label>
                    <div className="action-buttons">
                      <button
                        onClick={() => setPointsManagement(prev => ({ ...prev, actionType: 'add' }))}
                        className={`action-btn ${pointsManagement.actionType === 'add' ? 'active' : ''}`}
                      >
                        <Plus className="w-4 h-4" />
                        {t('add', language)}
                      </button>
                      <button
                        onClick={() => setPointsManagement(prev => ({ ...prev, actionType: 'remove' }))}
                        className={`action-btn ${pointsManagement.actionType === 'remove' ? 'active' : ''}`}
                      >
                        <Minus className="w-4 h-4" />
                        {t('remove', language)}
                      </button>
                      <button
                        onClick={() => setPointsManagement(prev => ({ ...prev, actionType: 'set' }))}
                        className={`action-btn ${pointsManagement.actionType === 'set' ? 'active' : ''}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                        {t('set', language)}
                      </button>
                    </div>
                  </div>

                  <div className="role-select-group">
                    <label className="role-select-label">
                      {t('pointsAmount', language)}
                    </label>
                    <input
                      type="number"
                      min="1"
                      placeholder={t('enterPointsAmount', language)}
                      value={pointsManagement.pointsAmount}
                      onChange={(e) => setPointsManagement(prev => ({ ...prev, pointsAmount: e.target.value }))}
                      className="role-search-input"
                    />
                  </div>

                  <div className="role-reason-group">
                    <label className="role-reason-label">
                      {t('reasonOptional', language)}
                    </label>
                    <textarea
                      value={pointsManagement.reason}
                      onChange={(e) => setPointsManagement(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder={t('enterReasonForPointsAdjustment', language)}
                      className="role-reason-textarea"
                      rows={2}
                    />
                  </div>

                  {pointsManagement.pointsAmount && parseInt(pointsManagement.pointsAmount) > 0 && (
                    <div className="points-preview">
                      <div className="role-change-preview">
                        <p className="role-change-preview-text">
                          <strong>{t('actionAdd', language)}:</strong> {pointsManagement.actionType === 'add' ? t('actionAdd', language) : pointsManagement.actionType === 'remove' ? t('actionRemove', language) : t('actionSet', language)} {pointsManagement.pointsAmount} {t('points', language)}
                        </p>
                        <p className="role-change-preview-description">
                          {t('newBalance', language)} {pointsManagement.actionType === 'set' 
                            ? `${pointsManagement.pointsAmount} ${t('points', language)}`
                            : `${pointsManagement.selectedUser.points + (pointsManagement.actionType === 'add' ? parseInt(pointsManagement.pointsAmount) : -parseInt(pointsManagement.pointsAmount))} ${t('points', language)}`
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={openConfirmModal}
                  disabled={loading || !pointsManagement.pointsAmount || parseInt(pointsManagement.pointsAmount) <= 0}
                  className="role-change-btn"
                >
                  {loading ? t('processing', language) : `${pointsManagement.actionType === 'add' ? t('addPoints', language) : pointsManagement.actionType === 'remove' ? t('removePoints', language) : t('setPoints', language)}`}
                </button>
              </>
            ) : (
              <div className="role-placeholder">
                <Coins className="role-placeholder-icon" />
                <p className="role-placeholder-text">{t('searchAndSelectUserPoints', language)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Points Confirmation Modal */}
      {pointsManagement.showConfirmModal && (
        <PointsConfirmationModal
          pointsManagement={pointsManagement}
          setPointsManagement={setPointsManagement}
          handlePointsManagement={handlePointsManagement}
          loading={loading}
          language={language}
          t={t}
        />
      )}
    </div>
  );
};

// Points Confirmation Modal
const PointsConfirmationModal = ({ 
  pointsManagement, 
  setPointsManagement, 
  handlePointsManagement, 
  loading,
  language,
  t
}) => {
  const confirmAction = () => {
    handlePointsManagement();
  };

  const closeModal = () => {
    setPointsManagement(prev => ({ ...prev, showConfirmModal: false }));
  };

  const actionText = pointsManagement.actionType === 'add' ? t('actionAdd', language) : pointsManagement.actionType === 'remove' ? t('actionRemove', language) : t('actionSet', language);
  const actionColor = pointsManagement.actionType === 'remove' ? 'danger' : 'primary';

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-icon">
            <Coins className="w-6 h-6 text-white" />
          </div>
          <h3 className="modal-title">{t('confirmPointsAction', language) || `Confirm ${actionText}`}</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-user-info">
            <p className="modal-user-name">{pointsManagement.selectedUser.inGameName}</p>
            <p className="modal-user-email">{pointsManagement.selectedUser.email}</p>
            <p className="modal-user-email">{t('currentPoints', language)}: {pointsManagement.selectedUser.points}</p>
          </div>

          <div className="modal-role-change">
            <div className="points-badge current">
              {pointsManagement.selectedUser.points} {t('pts', language)}
            </div>
            <div className="modal-role-arrow">→</div>
            <div className={`points-badge ${pointsManagement.actionType}`}>
              {pointsManagement.actionType === 'set' 
                ? `${pointsManagement.pointsAmount} ${t('pts', language)}`
                : `${pointsManagement.selectedUser.points + (pointsManagement.actionType === 'add' ? parseInt(pointsManagement.pointsAmount) : -parseInt(pointsManagement.pointsAmount))} ${t('pts', language)}`
              }
            </div>
          </div>

          <div className="modal-points-action">
            <p className="modal-reason-title">{t('pointsAction', language)}</p>
            <p className="modal-reason-text">
              {`${actionText} ${pointsManagement.pointsAmount} points ${pointsManagement.actionType === 'add' ? 'to' : pointsManagement.actionType === 'remove' ? 'from' : 'for'} ${pointsManagement.selectedUser.inGameName}`}
            </p>
          </div>

          {pointsManagement.reason && (
            <div className="modal-reason">
              <p className="modal-reason-title">{t('reason', language)}:</p>
              <p className="modal-reason-text">{pointsManagement.reason}</p>
            </div>
          )}

          <div className="modal-warning">
            <p className="modal-warning-text">
              {t('thisActionWillUpdatePoints', language) || 'This action will immediately update the user\'s points.'}
            </p>
          </div>
        </div>
        
        <div className="modal-actions">
          <button
            onClick={confirmAction}
            disabled={loading}
            className={`modal-btn modal-btn-${actionColor}`}
          >
            {loading ? t('processing', language) : `${t('confirmAction', language) || 'Confirm'} ${actionText}`}
          </button>
          <button
            onClick={closeModal}
            disabled={loading}
            className="modal-btn modal-btn-secondary"
          >
            {t('cancel', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PointsManagementTab;